import { useCallback, useEffect, useState } from 'react';

function isFalsy(value: QueryParamValue) {
  return value === null || value === '';
}
/**
 * URL 쿼리 파라미터 값 타입
 */
type QueryParamValue = string | null;

/**
 * URL 쿼리 파라미터 객체 타입
 */
type QueryParams = Record<string, string>;

/**
 * URL 쿼리 파라미터 설정 객체 타입
 */
type SetQueryParams = Record<string, QueryParamValue>;

/**
 * URL의 queryString을 저장소처럼 사용하는 훅
 * @returns 쿼리 문자열 관련 유틸리티 함수와 상태
 */
export const useQueryString = () => {
  // 브라우저 환경인지 확인
  const isBrowser = typeof window !== 'undefined';

  /**
   * 현재 URL에서 쿼리 파라미터를 파싱하는 함수
   */
  const parseQueryString = (): QueryParams => {
    if (!isBrowser) return {};

    const queryString = window.location.search;
    if (!queryString) return {};

    return Object.fromEntries(new URLSearchParams(queryString).entries());
  };

  // 현재 쿼리 파라미터 상태 관리
  const [queryParams, setQueryParams] = useState<QueryParams>(isBrowser ? parseQueryString() : {});

  /**
   * URL 업데이트 함수
   */
  const updateUrl = useCallback((newQueryString: string): void => {
    if (!isBrowser) return;

    const newPathWithQuery = `${window.location.pathname}${
      newQueryString ? `?${newQueryString}` : ''
    }`;

    // 히스토리 상태 업데이트 (URL 변경)
    window.history.pushState(
      {
        ...window.history.state,
        as: newPathWithQuery,
        url: newPathWithQuery,
      },
      '',
      newPathWithQuery,
    );
  }, []); // isBrowser는 상수이므로 의존성 배열에서 제거

  // 브라우저 히스토리 변경 시 쿼리 파라미터 상태 업데이트
  useEffect(() => {
    if (!isBrowser) return;

    const handlePopState = () => {
      setQueryParams(parseQueryString());
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  /**
   * 쿼리 파라미터 값 가져오기
   * @param key 가져올 쿼리 파라미터의 키
   * @returns 해당 키의 값 또는 null
   */
  const get = useCallback(
    (key: string): QueryParamValue => queryParams[key] || null,
    [queryParams], // isBrowser는 상수이므로 의존성 배열에서 제거
  );

  /**
   * 쿼리 파라미터 값 설정하기
   * @param key 설정할 쿼리 파라미터의 키
   * @param value 설정할 값 (null 또는 빈 문자열인 경우 해당 파라미터 제거)
   */
  const set = useCallback(
    (key: string, value: QueryParamValue): void => {
      if (!isBrowser) return;

      // 현재 URL 파싱
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);

      // 값이 null이거나 빈 문자열이면 파라미터 제거, 아니면 설정
      if (isFalsy(value)) {
        params.delete(key);
      } else {
        params.set(key, value);
      }

      // 새 쿼리스트링 생성
      const newQueryString = params.toString();

      // URL 업데이트
      updateUrl(newQueryString);

      // 내부 상태 업데이트
      setQueryParams((prev) => {
        const next = { ...prev };
        if (isFalsy(value)) {
          delete next[key];
        } else {
          next[key] = value;
        }
        return next;
      });
    },
    [updateUrl], // isBrowser는 상수이므로 의존성 배열에서 제거
  );

  /**
   * 여러 쿼리 파라미터 한 번에 설정하기
   * @param params 설정할 쿼리 파라미터 객체
   */
  const setMultiple = useCallback(
    (params: SetQueryParams): void => {
      if (!isBrowser) return;

      // 현재 URL 파싱
      const url = new URL(window.location.href);
      const urlParams = new URLSearchParams(url.search);

      // 모든 파라미터 설정
      Object.entries(params).forEach(([key, value]) => {
        if (isFalsy(value)) {
          urlParams.delete(key);
        } else {
          urlParams.set(key, value);
        }
      });

      // 새 쿼리스트링 생성
      const newQueryString = urlParams.toString();

      // URL 업데이트
      updateUrl(newQueryString);

      // 내부 상태 업데이트
      setQueryParams((prev) => {
        const next = { ...prev };
        Object.entries(params).forEach(([key, value]) => {
          if (isFalsy(value)) {
            delete next[key];
          } else {
            next[key] = value;
          }
        });
        return next;
      });
    },
    [updateUrl], // isBrowser는 상수이므로 의존성 배열에서 제거
  );

  /**
   * 모든 쿼리 파라미터 제거
   */
  const clear = useCallback((): void => {
    if (!isBrowser) return;

    // 쿼리스트링 없는 URL로 업데이트
    updateUrl('');

    // 내부 상태 초기화
    setQueryParams({});
  }, [updateUrl]); // isBrowser는 상수이므로 의존성 배열에서 제거

  return {
    get,
    set,
    setMultiple,
    clear,
    params: queryParams,
  };
};
